/**
 * music-video-webcodecs.js — hardware montage engine for /music-video-maker.
 *
 * Replaces the FFmpeg-WASM per-segment x264 encode (up to 48 libx264 exec()s,
 * single-threaded → 8-57 min renders and a 29 % completion rate on mobile,
 * telemetry 2026-07) with one decode → composite → encode pass on WebCodecs:
 *
 *   per-clip mp4box demux (cached) → VideoDecoder(hw) over each SEGMENT window
 *      → OffscreenCanvas (scale+pad letterbox, optional zoom-pulse punch-in)
 *      → CanvasVideoExport.encode (VideoEncoder hw + AAC of the song) → MP4
 *
 * Structurally this is video-merge-webcodecs.js without the transitions: the
 * beat grid is a list of segments, each a source-time window of one clip, laid
 * end to end. planSegments() already emits them; we just turn output-time into
 * (segment, source-time) and pull the frame. HW encode runs 60-200 fps vs the
 * ~3-5 fps single-thread WASM x264 (see canvas-video-export.js / webcodecs-
 * pipeline.js headers for the measured gap and why COEP/mt is NOT the answer).
 *
 * Reuses the proven, bounded decode primitives exposed by
 * window.VideoMergeWebCodecs (demux / DecodeStream / ClipCursor) and the shared
 * window.CanvasVideoExport encoder+muxer. Nothing here re-implements WebCodecs.
 *
 * SCOPE — the caller (music-video-maker.js) uses this as the PRIMARY path and
 * transparently falls back to the existing FFmpeg pipeline when probe() is not
 * ok. Deliberately NOT handled here (→ FFmpeg fallback):
 *   - per-clip keep-audio ducking (segs with keepAudio) — needs the sidechain
 *     mix; telemetry shows anyKept:0 across all real completions, so the fast
 *     path covers the song-only case and FFmpeg keeps the rare tier-2 renders.
 *   - non-mp4-family clips (mp4box demuxes ISOBMFF only) or codecs the hardware
 *     VideoDecoder rejects, or browsers without WebCodecs / mp4-muxer.
 *
 * Exposes window.MusicVideoWebCodecs:
 *   await probe(clips, opts) → { ok:bool, reason:string, hardware:bool, h264:'native'|'wasm', audioRemux:bool }
 *   await render(opts) → Blob            (throws on failure → caller falls back)
 *     opts: { clips:[{file,duration,w,h}], segs:[{clipIndex,start,dur,keepAudio}],
 *             song:{file, buffer}, W, H, fps, crf, effect:'none'|'zoom_pulse',
 *             musicVol:0..1, loopSong:bool, audioRemux:bool, onProgress:fn(frac), signal:AbortSignal }
 *
 * ── What this lane hands a person is H.264 — or the FFmpeg lane does (2026-10-30) ──
 * Report of 23.10 (Yandex 26 on Windows, no H.264 encoder in the browser at all:
 * device_cap vch2=0 vcs2=0): «видео со звуком совместились хорошо, но при
 * скачивании остаётся только звук». The exporter had answered the missing H.264
 * with VP9 inside the .mp4 (cve_vp9_rescue); the page played it, the person's
 * player did not. 33 of 528 finished clips in 30 days left like that. Since 29.10
 * the exporter carries x264 in WebAssembly for such browsers, and still falls to
 * VP9 when that rung is missing or dies (both reproduced on the stand). This
 * tool has an H.264 lane of its own — the FFmpeg montage — so the fast lane asks
 * for H.264 only: `h264Lane` in the probe, `h264Only` in the encode, and where
 * neither holds the montage goes to FFmpeg, slower and watchable.
 */
(function () {
'use strict';
if (window.MusicVideoWebCodecs) return;

function VMW() { return window.VideoMergeWebCodecs; }

function throwIfAborted(signal) {
    if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
}

/* scale=decrease + pad center (letterbox) — mirrors the FFmpeg vf
   `scale=W:H:force_original_aspect_ratio=decrease,pad=W:H:(ow-iw)/2:(oh-ih)/2`.
   `zoom` > 1 is a centred punch-in (the zoom_pulse effect); it enlarges the
   drawn frame past the frame edges, which the canvas clips — same visual as the
   FFmpeg zoompan on-cut punch. The black fill is drawn UNSCALED so letterbox
   bars stay black regardless of zoom. */
function drawClipFrame(ctx, frame, W, H, zoom) {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);
    if (!frame) return;
    var VO = window.VideoOrientation;
    /* Размер — display, а не coded: клип с телефона хранится ландшафтом с
       матрицей поворота, и по coded-размеру он лёг бы в кадр боком. */
    var _s = VO ? VO.size(frame) : { w: frame.displayWidth || frame.codedWidth, h: frame.displayHeight || frame.codedHeight };
    var sw = _s.w, sh = _s.h;
    if (!sw || !sh) return;
    var scale = Math.min(W / sw, H / sh) * (zoom || 1);
    var dw = sw * scale, dh = sh * scale;
    if (VO) VO.draw(ctx, frame, (W - dw) / 2, (H - dh) / 2, dw, dh);
    else ctx.drawImage(frame, (W - dw) / 2, (H - dh) / 2, dw, dh);
}

/* The same letterboxed frame painted OVER what is already on the canvas, at
   the caller's globalAlpha: the picture and only the bars around it, never a
   full black fill. Under an alpha `a` that gives A·(1−a) + B·a — exactly
   FFmpeg's `xfade=fade` of two letterboxed inputs; drawClipFrame's full fill
   would darken the outgoing shot twice. */
function drawClipFrameOver(ctx, frame, W, H) {
    if (!frame) return;
    var VO = window.VideoOrientation;
    var _s = VO ? VO.size(frame) : { w: frame.displayWidth || frame.codedWidth || frame.width, h: frame.displayHeight || frame.codedHeight || frame.height };
    var sw = _s.w, sh = _s.h;
    if (!sw || !sh) return;
    var scale = Math.min(W / sw, H / sh);
    var dw = sw * scale, dh = sh * scale, dx = (W - dw) / 2, dy = (H - dh) / 2;
    if (VO) VO.draw(ctx, frame, dx, dy, dw, dh);
    else ctx.drawImage(frame, dx, dy, dw, dh);
    ctx.fillStyle = '#000';
    if (dy > 0.5) { ctx.fillRect(0, 0, W, dy); ctx.fillRect(0, dy + dh, W, H - dy - dh); }
    if (dx > 0.5) { ctx.fillRect(0, dy, dx, dh); ctx.fillRect(dx + dw, dy, W - dx - dw, dh); }
}

/* Collage layouts — normalized [x,y,w,h] cells in [0,1], scaled to the output.
   Cell count === clip count the layout shows. Panels are cover-cropped to fill
   (like the /split-screen tool); the single 'full' layout keeps the letterbox
   look of the non-collage montage. planSegments (music-video-maker.js) picks a
   layout whose cell count ≤ the available clips and fills seg.cells to match. */
var LAYOUTS = {
    full: [[0, 0, 1, 1]],
    side: [[0, 0, 0.5, 1], [0.5, 0, 0.5, 1]],
    pip:  [[0, 0, 1, 1], [0.655, 0.63, 0.32, 0.34]],
    row3: [[0, 0, 1 / 3, 1], [1 / 3, 0, 1 / 3, 1], [2 / 3, 0, 1 / 3, 1]],
    grid: [[0, 0, 0.5, 0.5], [0.5, 0, 0.5, 0.5], [0, 0.5, 0.5, 0.5], [0.5, 0.5, 0.5, 0.5]]
};

/* Draw a frame into a dest rect with centre-crop cover (fills the cell, no black
   gaps) — the panel look in the reference videos. Mirrors split-screen fitRect
   'cover'. */
function drawCoverCell(ctx, frame, dx, dy, dw, dh) {
    ctx.fillStyle = '#000';
    ctx.fillRect(dx, dy, dw, dh);
    if (!frame) return;
    var VO = window.VideoOrientation;
    var _s = VO ? VO.size(frame) : { w: frame.displayWidth || frame.codedWidth, h: frame.displayHeight || frame.codedHeight };
    var sw = _s.w, sh = _s.h;
    if (!sw || !sh) return;
    var cellAR = dw / dh, srcAR = sw / sh, sx, sy, ssw, ssh;
    if (srcAR > cellAR) { ssh = sh; ssw = Math.round(sh * cellAR); sx = Math.round((sw - ssw) / 2); sy = 0; }
    else { ssw = sw; ssh = Math.round(sw / cellAR); sx = 0; sy = Math.round((sh - ssh) / 2); }
    sx = Math.max(0, Math.min(sx, sw - 1)); sy = Math.max(0, Math.min(sy, sh - 1));
    ssw = Math.max(1, Math.min(ssw, sw - sx)); ssh = Math.max(1, Math.min(ssh, sh - sy));
    try {
        if (VO) VO.drawSource(ctx, frame, sx, sy, ssw, ssh, dx, dy, dw, dh);
        else ctx.drawImage(frame, sx, sy, ssw, ssh, dx, dy, dw, dh);
    } catch (_) {}
}

function layoutOf(seg) {
    var id = (seg && seg.layout && LAYOUTS[seg.layout]) ? seg.layout : 'full';
    return { id: id, rects: LAYOUTS[id] };
}
function cellsOf(seg) {
    return (seg && seg.cells && seg.cells.length)
        ? seg.cells
        : [{ clipIndex: seg.clipIndex, start: seg.start }];
}

/* Song → one rendered AudioBuffer, at the requested volume, with a gentle
   true-peak-ish limiter (DynamicsCompressor near 0 dB) that stands in for the
   FFmpeg `alimiter=limit=0.94`. In sequential mode the montage can outlast the
   song, so the source loops to fill `totalSec` (parity with `-stream_loop -1`);
   in beat mode the montage tiles the song, so `totalSec` ≈ song length and a
   longer song is simply truncated by the render length.

   The song is NOT decoded again here (2026-10-30). The tool decodes it once,
   at upload, through AudioDecoderRobust (WebCodecs → native → FFmpeg) and
   keeps the AudioBuffer it beat-tracks; this lane used to read the file a
   second time with a bare decodeAudioData, which refuses what only the robust
   ladder reads — prod, 30 d: 10 sessions fell from here to the FFmpeg lane
   with `mvmwc:song-decode-failed` (2 never got a clip), on a song the page had
   already played. The render runs at the buffer's own rate, so the PCM is not
   resampled a second time either (an AudioBufferSourceNode bridges rates by
   interpolation, not by the decoder's resampler). `song` may still be a bare
   File/Blob — the harness door `_buildSongBuffer` — and then it is decoded
   through the same robust ladder. */
var AAC_RATES = { 44100: 1, 48000: 1 };
async function decodeSong(song) {
    if (song && song.buffer && song.buffer.length) return song.buffer;
    var file = (song && song.file) || song;
    if (!file || typeof file.arrayBuffer !== 'function') throw new Error('mvmwc:no-song');
    var AC = window.AudioContext || window.webkitAudioContext;
    var ctx = null;
    try {
        if (window.AudioDecoderRobust && AC) {
            ctx = new AC();
            var res = await window.AudioDecoderRobust.robust(file, ctx, { slug: 'music-video-maker' });
            if (res && res.buffer) return res.buffer;
        }
        var OAC0 = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        return await new OAC0(2, 1, 48000).decodeAudioData((await file.arrayBuffer()).slice(0));
    } catch (e) {
        throw new Error('mvmwc:song-decode-failed');
    } finally { try { ctx && ctx.close(); } catch (_) {} }
}
/* A song decoded at a rate AAC does not take everywhere (the robust ladder's
   WebCodecs and FFmpeg tiers keep the file's own rate: 22.05, 24, 32 kHz…) goes
   to 48 kHz through the site's converter, not through the render: an
   AudioBufferSourceNode bridges rates by LINEAR interpolation (audio-resample.js
   header, measured). The converter loads on first need; without it the render
   bridges as before. */
function loadResampler() {
    if (window.AudioResample) return Promise.resolve(true);
    if (!window.__mvmResampleLoading) {
        window.__mvmResampleLoading = new Promise(function (resolve) {
            var s = document.createElement('script');
            s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/audio-resample.js') : '/js/audio-resample.js';
            s.onload = function () { resolve(!!window.AudioResample); };
            s.onerror = function () { window.__mvmResampleLoading = null; resolve(false); };
            document.head.appendChild(s);
        });
    }
    return window.__mvmResampleLoading;
}
async function toRate(buf, rate) {
    if (buf.sampleRate === rate) return buf;
    try {
        if (await loadResampler()) {
            var out = await window.AudioResample.resample(buf, rate);
            if (out && out.sampleRate === rate && typeof out.getChannelData === 'function') return out;
        }
    } catch (_) {}
    return buf;
}
async function buildSongBuffer(song, totalSec, vol, loop, fade) {
    var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!OAC) throw new Error('mvmwc:no-offline-audio');
    var decoded = await decodeSong(song);
    // AAC takes 44.1 and 48 kHz everywhere; anything else is rendered at 48.
    var SR = AAC_RATES[decoded.sampleRate] ? decoded.sampleRate : 48000;
    decoded = await toRate(decoded, SR);
    var frames = Math.max(1, Math.ceil(totalSec * SR));
    // The limiter (DynamicsCompressor) holds a 6 ms look-ahead: rendered exactly
    // `frames` long the song came out 288 samples late against the cuts and lost
    // the end of its fade. Render longer by the measured latency, drop the head
    // (NodeLatency, 2026-10-09).
    var lead = window.NodeLatency ? await window.NodeLatency.compressor(SR) : 0;
    var octx = new OAC(2, frames + lead, SR);

    var src = octx.createBufferSource();
    src.buffer = decoded;
    src.loop = !!loop;

    var gain = octx.createGain();
    var v = (vol == null ? 1 : Math.max(0, vol));
    gain.gain.value = v;
    if (fade && totalSec > 1) {
        // Gentle edges: 0.25 s in, 1.2 s out (clamped for tiny montages) —
        // mirrors the afade pair the FFmpeg fallback applies for the same toggle.
        var fin = Math.min(0.25, totalSec / 4), fout = Math.min(1.2, totalSec / 3);
        gain.gain.setValueAtTime(0.0001, 0);
        gain.gain.linearRampToValueAtTime(v, fin);
        gain.gain.setValueAtTime(v, Math.max(fin, totalSec - fout));
        gain.gain.linearRampToValueAtTime(0.0001, totalSec);
    }

    var comp = octx.createDynamicsCompressor();
    comp.threshold.value = -1.5;
    comp.knee.value = 0;
    comp.ratio.value = 20;
    comp.attack.value = 0.003;
    comp.release.value = 0.05;

    src.connect(gain);
    gain.connect(comp);
    comp.connect(octx.destination);
    src.start(0);

    var rendered = await octx.startRendering();
    return lead ? window.NodeLatency.trimHead(rendered, lead, frames) : rendered;
}

/* ── How much of a clip may sit in memory (2026-10-30) ──
   The render decodes clips through the shared lane door (demuxClip): the
   sample TABLE from ftyp+moov, the sample BYTES read from disk in ≤ 8 MB
   windows as the decoder needs them — memory O(1) per clip, whatever its size.
   Until today this lane used the eager demux, which reads the whole file and
   keeps a copy of every sample (≈ 2.7 × the file) for the whole render, and
   guarded it with a budget on Σ file sizes. Prod, 30 d: 19 renders were sent
   to the FFmpeg lane by that budget (6 of them never got a clip: FFmpeg runs
   ~11× real time, a phone does not wait half an hour), and on phones a project
   of 400 MB+ that passed it ended with no clip at all in 1 of 3 renders — the
   tab gone, no event after the start. The budget now prices only the clips the
   lazy table cannot read (a file whose moov the box walk does not find — an
   eager read is the only way in): over it, the FFmpeg lane, which streams
   from disk. `window.__mvmWcBudgetMB` overrides it for tests. */
function eagerBudgetBytes() {
    var mb = (typeof window.__mvmWcBudgetMB === 'number') ? window.__mvmWcBudgetMB : (function () {
        var dm = navigator.deviceMemory;
        var ios = /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
            (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
        if (ios) return 350;
        if (typeof dm === 'number') { if (dm >= 8) return 1200; if (dm >= 6) return 800; return 450; }
        return 800;   // desktop Safari/Firefox: no deviceMemory, ample RAM
    })();
    return mb * 1048576;
}
var SLUG = 'music-video-maker';

/* One clip's codec, description and size, as the decoder will be configured. */
async function clipVerdict(vmw, d) {
    if (!d.desc && /avc1|h264|hvc1|hev1/i.test(d.codec)) return 'avcc-missing';
    if (typeof vmw.hasUsableDims === 'function' && !vmw.hasUsableDims(d)) return 'no-dims';
    try {
        var sup = await VideoDecoder.isConfigSupported(vmw.decoderConfig(d));
        if (!sup || !sup.supported) return 'decoder-unsupported:' + d.codec;
    } catch (e) { return 'decoder-probe-threw'; }
    return '';
}

/* Every clip mp4-family AND decodable, H.264 available for the output (the
   browser's own or the exporter's x264), the muxer present, and NO keep-audio
   segment. Any miss → { ok:false, reason } and the caller runs the FFmpeg
   pipeline unchanged. A browser with VideoEncoder and no AudioEncoder (iOS
   16.4–18) is not a miss: the picture goes through here and the shared remux
   door glues the song on (`audioRemux`). */
async function probe(clips, opts) {
    opts = opts || {};
    var vmw = VMW();
    var CVE = window.CanvasVideoExport;
    if (window.__mvmForceLane === 'ffmpeg') return { ok: false, reason: 'forced-ffmpeg' };   // test handle
    if (!vmw || typeof vmw.demux !== 'function') return { ok: false, reason: 'no-video-merge-primitives' };
    if (!CVE || typeof CVE.isSupported !== 'function') return { ok: false, reason: 'no-canvas-export' };
    if (typeof VideoDecoder === 'undefined' || typeof VideoEncoder === 'undefined' ||
        typeof VideoFrame === 'undefined') {
        return { ok: false, reason: 'no-webcodecs' };
    }
    if (!clips || !clips.length) return { ok: false, reason: 'no-clips' };

    /* Stills are not decoded at all — they are drawn. Sending one down the
       container/decoder checks would drop a whole project onto the FFmpeg lane
       over a JPEG (5x slower: 822 s vs 164 s average, 90 d). */
    for (var i = 0; i < clips.length; i++) {
        if (clips[i].isImage) continue;
        if (!vmw.isMp4Family(clips[i].file)) return { ok: false, reason: 'non-mp4-container' };
    }
    try { await vmw.loadMp4Box(); } catch (e) { return { ok: false, reason: 'no-mp4box' }; }

    /* Each clip read the way the render will read it: the lazy table first
       (ftyp + moov, nothing else). Only a clip it cannot read needs the eager
       demux — and only those are priced against memory, BEFORE any is read. */
    var eager = [], eagerBytes = 0;
    for (var j = 0; j < clips.length; j++) {
        if (clips[j].isImage) continue;
        var d = null;
        if (typeof vmw.demuxLazy === 'function') {
            try { d = await vmw.demuxLazy(clips[j].file); } catch (e) { d = null; }
        }
        if (!d) { eager.push(j); eagerBytes += (clips[j].file && clips[j].file.size) || 0; continue; }
        var why = await clipVerdict(vmw, d);
        if (why) return { ok: false, reason: why };
    }
    if (eagerBytes > eagerBudgetBytes()) return { ok: false, reason: 'memory-budget' };
    for (var k = 0; k < eager.length; k++) {
        var de;
        try { de = await vmw.demux(clips[eager[k]].file, true); }
        catch (e) { return { ok: false, reason: 'demux-failed' }; }
        var whyE = await clipVerdict(vmw, de);
        if (whyE) return { ok: false, reason: whyE };
    }

    /* H.264 or nothing (see the header): 'native' — the browser's encoder made
       a real frame; 'wasm' — only the exporter's x264 did; null — neither, and
       the FFmpeg lane's libx264 makes the clip instead of VP9 in an .mp4. */
    var lane = null;
    try { lane = typeof CVE.h264Lane === 'function' ? await CVE.h264Lane(opts.W, opts.H) : null; } catch (e) { lane = null; }
    if (!lane) return { ok: false, reason: 'no-h264' };

    // The muxer, and AAC at the rate the song will be rendered at.
    var vidOk = await CVE.isSupported({
        format: 'mp4', width: opts.W, height: opts.H, fps: opts.fps || 30,
        audioSampleRate: opts.audioRate || 48000, audioChannels: 2
    });
    if (!vidOk.ok) {
        var remux = !!(CVE.canRemuxAudioFor && CVE.canRemuxAudioFor(vidOk.reason) &&
                       typeof CVE.muxAudioInto === 'function' && window.FFmpegProcessor);
        if (!remux) return { ok: false, reason: 'cve:' + (vidOk.reason || 'unknown') };
        return { ok: true, reason: 'ok', hardware: false, h264: lane, audioRemux: true };
    }
    return { ok: true, reason: 'ok', hardware: !!vidOk.hardware, h264: lane, audioRemux: false };
}

async function render(opts) {
    var vmw = VMW();
    var clips = opts.clips, segs = opts.segs;
    var W = opts.W, H = opts.H, fps = opts.fps || 30;
    var signal = opts.signal;
    var effect = opts.effect;

    if (!segs || !segs.length) throw new Error('mvmwc:no-segments');

    // Output timeline: each segment laid end to end.
    var outStart = [], acc = 0;
    for (var i = 0; i < segs.length; i++) { outStart[i] = acc; acc += segs[i].dur; }
    var total = acc;
    if (!(total > 0.05)) throw new Error('mvmwc:empty-timeline');

    function segIndexAt(tSec) {
        var lo = 0, hi = segs.length - 1, ans = 0;
        while (lo <= hi) {
            var m = (lo + hi) >> 1;
            if (outStart[m] <= tSec + 1e-9) { ans = m; lo = m + 1; } else hi = m - 1;
        }
        return ans;
    }

    // Demux each clip once per render through the lane door: the sample table
    // now, the bytes from disk window by window (see eagerBudgetBytes). Beat mode
    // revisits clips throughout the montage; the table is what is cached, not
    // the file. A clip the lazy table cannot read is read eagerly, under the
    // same budget the probe priced.
    /* The bytes come from the copy the uploader made at pick time where the
       live handle can die on its own (FileBytes.settle: Android content
       providers, the iOS Photos picker — prod, 30 d: 8 renders lost to FILE_LOST
       / NotReadableError). Read lazily, a clip is read for the whole render,
       not once at its start, so the door matters more than it did. A desktop
       gets the live file at once (settle does not wait for a copy there). */
    function durable(file) {
        var FB = window.FileBytes;
        if (!FB || typeof FB.settle !== 'function') return Promise.resolve(file);
        return Promise.resolve().then(function () { return FB.settle(file); })
            .then(function (f) { return f || file; }, function () { return file; });
    }
    var demuxCache = {};
    function getDemux(ci) {
        if (!demuxCache[ci]) {
            var p = demuxCache[ci] = durable(clips[ci].file).then(function (f) {
                return typeof vmw.demuxClip === 'function'
                    ? vmw.demuxClip(f, false, { slug: SLUG, eagerMax: eagerBudgetBytes() })
                    : vmw.demux(f);
            });
            // a refusal is not kept: the cell's next opening asks the file again
            p.catch(function () { if (demuxCache[ci] === p) delete demuxCache[ci]; });
        }
        return demuxCache[ci];
    }

    /* Stills: one decoded bitmap per clip, owned by the RENDER. The cursor
       below deliberately does NOT close it — ClipCursor.close() closes the
       frame it handed out, and a shared bitmap closed after the first segment
       would blank the picture for every later one. */
    var bmpCache = {};
    async function getBitmap(ci) {
        if (!bmpCache[ci]) bmpCache[ci] = durable(clips[ci].file).then(function (f) { return createImageBitmap(f); });
        return await bmpCache[ci];
    }
    function stillCursor(bmp) {
        return { frameAt: function () { return Promise.resolve(bmp); }, close: function () { return Promise.resolve(); } };
    }

    // A fresh short-lived DecodeStream per CELL per segment over that cell's
    // [start,end] source window. Because cursors are never carried across
    // segments (each segment rebuilds its cells), there is no monotonic-source
    // constraint at all — a cell just seeks to the last keyframe ≤ its start and
    // decodes forward. A full-frame segment has one cell; a collage segment has
    // up to 4 (grid), so at most 4 decoders are live at once — within browser
    // decoder limits, and each frame is closed the instant it is drawn.
    /* «Наплыв» (opts.transition = 'dissolve', 2026-11-04): the last `xd[i]`
       seconds of segment i blend into segment i+1, ending ON the cut — the rule
       is MusicVideoTransition's, shared with the FFmpeg lane and the preview.
       The incoming cell's cursor opens EARLY, on a window that starts `xd`
       before its own start, and is handed to its segment at the cut — so the
       incoming shot keeps moving across the junction and no second decoder is
       opened for it. */
    var MVT = window.MusicVideoTransition;
    var xd = segs.map(function (s, i) {
        return MVT ? MVT.dur(opts.transition, segs, i, function (sg) { return layoutOf(sg).rects.length > 1; }) : 0;
    });
    var nextCursor = null, nextCursorFor = -1;

    var curSegIdx = -1, curCursors = [];
    async function closeCursors() {
        for (var q = 0; q < curCursors.length; q++) { try { if (curCursors[q]) await curCursors[q].close(); } catch (_) {} }
        curCursors = [];
    }
    async function closeNext() {
        if (nextCursor) { try { await nextCursor.close(); } catch (_) {} }
        nextCursor = null; nextCursorFor = -1;
    }
    async function ensureSeg(sIdx) {
        if (sIdx === curSegIdx) return;
        await closeCursors();
        var seg = segs[sIdx];
        var rects = layoutOf(seg).rects;
        var cells = cellsOf(seg);
        for (var c = 0; c < rects.length; c++) {
            var cell = cells[c] || cells[0];
            // stills are drawn from the render's bitmap; a video cell opens on its first frame (cellFrame)
            curCursors.push((clips[cell.clipIndex] && clips[cell.clipIndex].isImage)
                ? stillCursor(await getBitmap(cell.clipIndex)) : null);
        }
        // the incoming shot of the dissolve already has its cursor — it carries on
        if (nextCursor && nextCursorFor === sIdx && curCursors.length === 1 && curCursors[0] === null) {
            curCursors[0] = nextCursor; nextCursor = null; nextCursorFor = -1;
        } else if (nextCursorFor !== -1 && nextCursorFor <= sIdx) {
            await closeNext();
        }
        curSegIdx = sIdx;
    }
    async function openCell(seg, cell, leadS) {
        var d = await getDemux(cell.clipIndex);
        var startUs = Math.round(Math.max(0, cell.start - (leadS || 0)) * 1e6);
        var endUs = Math.round((cell.start + seg.dur) * 1e6);
        // drawClipFrame/drawCoverCell рисуют через VideoOrientation — копия не нужна
        return new vmw.ClipCursor(new vmw.DecodeStream(d, startUs, endUs, { deferRotation: true }), fps);
    }

    /* ── A cell whose decoder will not start is opened again — after a pause (2026-10-30) ──
       Every segment opens a FRESH decoder for its window. On Android that is
       where the montage died: prod, 30 d, 14 renders left the fast lane with
       «Cannot call 'decode' on a closed codec», 9 of them never got a clip (the
       FFmpeg lane they fell to runs ~11× real time). Each one had decoded the
       earlier segments of the same render; the decoder of a NEW segment failed
       on its first chunk (out:0), and the stream's own rescue — a fresh
       hardware decoder at once, then the software one, which Android does not
       have — failed with it, in the same millisecond. Fleet-wide a rescue on
       the first chunk almost never helps right away (decode_stream_rescued
       n=1 ≈ n=2 at «first»; mid-stream ~60 % do): what the device lacks at
       that moment is a decoder (the GPU process restarting, a codec still
       being released), and that passes. So the cell is opened again after a
       pause, from its own window — the cursor walks to the frame it was asked
       for, nothing is drawn twice — and the montage gives up to the FFmpeg lane
       only after the last pause. `window.__mvmReopenPauseMs` sets the pauses
       for stands. */
    var REOPEN_PAUSE_MS = (window.__mvmReopenPauseMs && window.__mvmReopenPauseMs.length) ? window.__mvmReopenPauseMs : [600, 2000, 5000];
    function pauseFor(ms) {
        return new Promise(function (resolve) {
            var t = setTimeout(resolve, ms);
            if (signal) signal.addEventListener('abort', function () { clearTimeout(t); resolve(); }, { once: true });
        });
    }
    async function cellFrame(c, seg, cell, tUs, sIdx) {
        for (var attempt = 0; ; attempt++) {
            try {
                if (!curCursors[c]) curCursors[c] = await openCell(seg, cell);
                return await curCursors[c].frameAt(tUs);
            } catch (e) {
                throwIfAborted(signal);
                if ((e && e.name === 'AbortError') || attempt >= REOPEN_PAUSE_MS.length) throw e;
                try { if (curCursors[c]) await curCursors[c].close(); } catch (_) {}
                curCursors[c] = null;
                try {
                    window.__ae && window.__ae('feature_use', { slug: SLUG, f: 'mvm_cell_reopened', n: attempt + 1, seg: sIdx,
                        ms: REOPEN_PAUSE_MS[attempt], err: String((e && e.message) || e || '').slice(0, 80) });
                } catch (_) {}
                await pauseFor(REOPEN_PAUSE_MS[attempt]);
                throwIfAborted(signal);
            }
        }
    }

    // The incoming frame of a dissolve at source second `srcS`. A still is its
    // bitmap; a video gets the early cursor, reopened after the same pauses as
    // a cell (cellFrame) — a junction is where Android loses decoders too.
    async function incomingFrame(nIdx, srcS) {
        var nseg = segs[nIdx], ncell = cellsOf(nseg)[0];
        if (clips[ncell.clipIndex] && clips[ncell.clipIndex].isImage) return await getBitmap(ncell.clipIndex);
        for (var attempt = 0; ; attempt++) {
            try {
                if (!nextCursor || nextCursorFor !== nIdx) {
                    await closeNext();
                    nextCursor = await openCell(nseg, ncell, xd[nIdx - 1]);
                    nextCursorFor = nIdx;
                }
                return await nextCursor.frameAt(Math.round(srcS * 1e6));
            } catch (e) {
                throwIfAborted(signal);
                if ((e && e.name === 'AbortError') || attempt >= REOPEN_PAUSE_MS.length) throw e;
                await closeNext();
                await pauseFor(REOPEN_PAUSE_MS[attempt]);
                throwIfAborted(signal);
            }
        }
    }

    var drawFrame = async function (ctx, idx, tSec) {
        throwIfAborted(signal);
        var sIdx = segIndexAt(tSec);
        await ensureSeg(sIdx);
        var seg = segs[sIdx];
        var rects = layoutOf(seg).rects;
        var cells = cellsOf(seg);
        var into = Math.max(0, tSec - outStart[sIdx]);   // seconds into this segment
        var multi = rects.length > 1;

        // zoom-pulse punch-in only for the single full-frame cell — a busy collage
        // frame doesn't need it. Mirrors FFmpeg zoompan z=if(eq(on,0),1.12,…).
        var zoom = (!multi && effect === 'zoom_pulse')
            ? Math.max(1.0, 1.12 - 0.012 * Math.round(into * fps)) : 1;

        if (multi) { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H); }  // gaps stay black
        for (var c = 0; c < rects.length; c++) {
            var cell = cells[c] || cells[0];
            var frame = await cellFrame(c, seg, cell, Math.round((cell.start + into) * 1e6), sIdx);
            if (multi) {
                var r = rects[c];
                drawCoverCell(ctx, frame, Math.round(r[0] * W), Math.round(r[1] * H),
                    Math.round(r[2] * W), Math.round(r[3] * H));
            } else {
                drawClipFrame(ctx, frame, W, H, zoom);   // full: letterbox (+zoom)
            }
        }

        // «Наплыв»: the next shot over this one's tail, without the beat effect
        // (the punch-in and the flash belong to the cut itself) — as the FFmpeg
        // lane's `xfade` input.
        var remain = seg.dur - into;
        if (xd[sIdx] > 0 && remain < xd[sIdx]) {
            var nIdx = sIdx + 1;
            var nf = await incomingFrame(nIdx, MVT.incomingAt(cellsOf(segs[nIdx])[0], remain));
            ctx.save();
            ctx.globalAlpha = MVT.alpha(remain, xd[sIdx]);
            drawClipFrameOver(ctx, nf, W, H);
            ctx.restore();
        }

        // flash: white burst on every cut, decaying over the first ~130 ms —
        // the classic beat-flash. Whole-frame, so collage segments flash too.
        // Mirrors the `fade=t=in:st=0:d=0.13:color=white` the FFmpeg path uses.
        if (effect === 'flash') {
            var fa = 1 - into / 0.13;
            if (fa > 0) {
                ctx.globalAlpha = Math.min(1, fa) * 0.85;
                ctx.fillStyle = '#fff';
                ctx.fillRect(0, 0, W, H);
                ctx.globalAlpha = 1;
            }
        }
    };

    // Song audio (loops in sequential mode to outlast the montage).
    var songBuffer = await buildSongBuffer(opts.song, total, opts.musicVol, opts.loopSong, opts.musicFade);
    throwIfAborted(signal);

    var CVE = window.CanvasVideoExport;
    var remux = !!opts.audioRemux;
    var blob;
    try {
        blob = await CVE.encode({
            width: W, height: H, fps: fps, durationSec: total, format: 'mp4',
            qualityCrf: opts.crf,
            // no AudioEncoder in this browser (iOS 16.4–18): the picture here, the song glued on below
            audioBuffer: remux ? null : songBuffer,
            // H.264 or a typed refusal — never VP9 in the .mp4 (see the header); the caller has FFmpeg
            h264Only: true,
            drawFrame: drawFrame, signal: signal,
            onProgress: opts.onProgress
        });
    } finally {
        await closeCursors();
        await closeNext();
        demuxCache = {};
    }
    if (remux) {
        throwIfAborted(signal);
        if (opts.onProgress) { try { opts.onProgress(0.96); } catch (_) {} }
        // The shared door: video copied, the song encoded to AAC, length = the picture's.
        var muxed = await CVE.muxAudioInto(blob, songBuffer, { format: 'mp4', signal: signal });
        if (!muxed) {
            try { CVE.release && CVE.release(blob); } catch (_) {}
            throwIfAborted(signal);
            throw new Error('mvmwc:audio-remux-failed');
        }
        blob = muxed;
    }
    return blob;
}

window.MusicVideoWebCodecs = {
    probe: probe,
    render: render,
    _drawClipFrame: drawClipFrame,   // exposed for test harnesses
    _buildSongBuffer: buildSongBuffer,
    version: '1.1.0'
};

})();
